import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hiddenField", "results", "clear"]
  static values = {
    url: { type: String, default: "/clients.json" },
    noResults: String,
    loading: String,
    minChars: { type: Number, default: 1 },
    submitOnSelect: { type: Boolean, default: false },
    debounce: { type: Number, default: 200 },
  }

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.activeIndex = -1
    this.toggleClearButton()
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
    if (this.fetchTimer) clearTimeout(this.fetchTimer)
  }

  search() {
    this.toggleClearButton()
    this.detachSelection()

    if (this.fetchTimer) clearTimeout(this.fetchTimer)
    this.fetchTimer = setTimeout(() => this.runFetch(), this.debounceValue)
  }

  runFetch() {
    const query = this.inputTarget.value.trim()
    if (query.length < this.minCharsValue) {
      this.hideResults()
      return
    }

    this.renderLoading()
    this.openResults()

    fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => response.ok ? response.json() : [])
      .then((clients) => this.renderResults(clients))
      .catch(() => this.renderResults([]))
  }

  renderLoading() {
    this.resultsTarget.innerHTML = `<li class="px-3 py-2 text-sm text-gray-500">${this.loadingValue}</li>`
  }

  renderResults(clients) {
    if (clients.length === 0) {
      this.resultsTarget.innerHTML = `<li class="px-3 py-2 text-sm text-gray-500">${this.noResultsValue}</li>`
      return
    }

    this.resultsTarget.innerHTML = clients
      .map((client, index) => {
        const subtitle = client.tax_id ? `<span class="text-xs text-gray-500 ml-2">${this.escape(client.tax_id)}</span>` : ""
        return `<li class="px-3 py-2 text-sm cursor-pointer hover:bg-indigo-50 flex items-center justify-between"
                    data-action="mousedown->client-selector#select"
                    data-client-selector-id-param="${this.escape(client.id)}"
                    data-client-selector-name-param="${this.escape(client.name)}"
                    data-index="${index}"><span>${this.escape(client.name)}</span>${subtitle}</li>`
      })
      .join("")
    this.activeIndex = -1
  }

  select(event) {
    if (event) event.preventDefault()
    const { id, name } = event.params
    this.applySelection(id, name)
    this.hideResults()
    if (this.submitOnSelectValue) this.submitForm()
  }

  applySelection(id, name) {
    this.hiddenFieldTarget.value = id
    this.inputTarget.value = name
    this.toggleClearButton()
    this.hiddenFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  detachSelection() {
    if (this.hiddenFieldTarget.value !== "") {
      this.hiddenFieldTarget.value = ""
      this.hiddenFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  clear(event) {
    if (event) event.preventDefault()
    this.inputTarget.value = ""
    this.hiddenFieldTarget.value = ""
    this.hideResults()
    this.toggleClearButton()
    this.inputTarget.focus()
    if (this.submitOnSelectValue) this.submitForm()
  }

  focus() {
    if (this.inputTarget.value.trim().length >= this.minCharsValue) {
      this.runFetch()
    }
  }

  blur() {
    // Hide is handled by outside-click; keep blur a no-op so mousedown on a result fires first.
  }

  keydown(event) {
    if (this.resultsTarget.classList.contains("hidden")) return
    const items = this.resultItems()
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1)
        this.highlight(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
        this.highlight(items)
        break
      case "Enter":
        if (this.activeIndex >= 0) {
          event.preventDefault()
          const item = items[this.activeIndex]
          this.applySelection(item.dataset.clientSelectorIdParam, item.dataset.clientSelectorNameParam)
          this.hideResults()
          if (this.submitOnSelectValue) this.submitForm()
        }
        break
      case "Escape":
        this.hideResults()
        break
    }
  }

  resultItems() {
    return Array.from(this.resultsTarget.querySelectorAll("li[data-action]"))
  }

  highlight(items) {
    items.forEach((item, idx) => {
      item.classList.toggle("bg-indigo-50", idx === this.activeIndex)
    })
  }

  openResults() {
    this.resultsTarget.classList.remove("hidden")
    document.addEventListener("click", this.handleOutsideClick)
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.activeIndex = -1
    document.removeEventListener("click", this.handleOutsideClick)
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.hideResults()
  }

  toggleClearButton() {
    if (!this.hasClearTarget) return
    const hasValue = this.inputTarget.value.length > 0 || this.hiddenFieldTarget.value.length > 0
    this.clearTarget.classList.toggle("hidden", !hasValue)
  }

  submitForm() {
    const form = this.element.closest("form")
    if (form) form.requestSubmit()
  }

  escape(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
